import { notifier } from "../../core/notifier/notifier";
import { ScrollLock } from "../../core/scroll-lock";
import { ModalNestedPosition } from "../../util/modal-nested-position";
import { spinner } from "../../util/spinner/spinner";
import {
    AdsAdminApi,
    type AdsAdminCreatePayload,
    type AdsAdminCreateResponse,
    type AdsAdminDeleteResponse,
    type AdsAdminUpdateResponse,
    type AdsAdminUploadImageResponse,
} from "./ads-admin-api";

interface AdsAdminFormLike {
    getRoot(): HTMLElement;
    getEditButton(root: EventTarget | Element | null | undefined): HTMLButtonElement | null;
    getDeleteButton(root: EventTarget | Element | null | undefined): HTMLButtonElement | null;
    getBannerTrigger(root: EventTarget | Element | null | undefined): HTMLButtonElement | null;
    getBannerInput(root: EventTarget | Element | null | undefined): HTMLInputElement | null;
    getRow(root: EventTarget | Element | null | undefined): HTMLFormElement | null;
    getSubmitButton(root: ParentNode): HTMLButtonElement | null;
    getField(root: ParentNode, name: string): HTMLElement | null;
    getFieldDisplay(root: ParentNode, field: string): HTMLElement | null;
    getBannerInputForRow(row: ParentNode): HTMLInputElement | null;
    getBannerPreviewImg(row: ParentNode): HTMLImageElement | null;
    getBannerStatus(row: ParentNode): HTMLElement | null;
    getBannerTriggerForRow(row: ParentNode): HTMLButtonElement | null;
    getCreateModal(): HTMLElement | null;
    getCreateForm(): HTMLFormElement | null;
    getCreateAdTypeSelect(): HTMLSelectElement | null;
    getCreateAdTitleInput(): HTMLInputElement | null;
    getCreateAdBodyInput(): HTMLTextAreaElement | null;
    getCreateLinkUrlInput(): HTMLInputElement | null;
    getCreateProductIdInput(): HTMLInputElement | null;
    getCreateDisplayRateInput(): HTMLInputElement | null;
    getCreateBannerInput(): HTMLInputElement | null;
    getCreateSubmitButton(): HTMLButtonElement | null;
}

const TEXT_FIELDS = ["ad_type", "ad_title", "ad_body", "link_url", "ad_product_id", "display_rate_percent"] as const;

const AD_TYPE_BADGE_COLORS: Record<string, string> = {
    PLACE_HOLDER: "badge-config--muted",
    PRODUCT_LINKER: "badge-config--info",
    PRODUCT_LINKER_OGP: "badge-config--info",
    PRODUCT_CARD: "badge-config--accent",
    TOP_BANNER: "badge-config--warning",
    SECTION_BANNER: "badge-config--warning",
    USERS_BANNER: "badge-config--success",
    USERS_BANNER_RECTANGLE: "badge-config--success",
    AUTH_BANNER: "badge-config--accent",
};

const BADGE_COLOR_CLASSES = ["badge-config--muted", "badge-config--info", "badge-config--accent", "badge-config--warning", "badge-config--success"];

export class AdsAdminHandler {
    public form: AdsAdminFormLike;

    public constructor(form: AdsAdminFormLike) {
        this.form = form;
    }

    public handleListClick(event: MouseEvent): void {
        const target = event.target;
        if (!(target instanceof Element)) {
            return;
        }

        const deleteButton = this.form.getDeleteButton(target);
        if (deleteButton instanceof HTMLButtonElement) {
            event.preventDefault();
            void this.deleteRow(deleteButton);
            return;
        }

        const bannerTrigger = this.form.getBannerTrigger(target);
        if (bannerTrigger instanceof HTMLButtonElement) {
            event.preventDefault();
            const row = this.form.getRow(bannerTrigger);
            const input = row ? this.form.getBannerInputForRow(row) : null;
            input?.click();
            return;
        }

        const editButton = this.form.getEditButton(target);
        if (editButton instanceof HTMLButtonElement) {
            event.preventDefault();
            const row = this.form.getRow(editButton);
            if (row instanceof HTMLFormElement) {
                row.dataset.editing = row.dataset.editing === "1" ? "0" : "1";
            }
        }
    }

    public handleListChange(event: Event): void {
        const target = event.target;
        if (!(target instanceof HTMLInputElement) || target.type !== "file") {
            return;
        }

        const bannerInput = this.form.getBannerInput(target);
        if (!(bannerInput instanceof HTMLInputElement)) {
            return;
        }

        void this.uploadBannerForRow(bannerInput);
    }

    private async uploadBannerForRow(input: HTMLInputElement): Promise<void> {
        const row = this.form.getRow(input);
        const file = input.files?.[0];
        if (!(row instanceof HTMLFormElement) || !file) {
            return;
        }

        const adId = Number.parseInt(row.dataset.adId || "", 10);
        if (!Number.isFinite(adId) || adId <= 0) {
            return;
        }

        const status = this.form.getBannerStatus(row);
        if (status) {
            status.textContent = "Uploading…";
        }

        const trigger = this.form.getBannerTriggerForRow(row);
        if (trigger instanceof HTMLButtonElement) {
            spinner.showButtonLoader(trigger, { translateKey: "spinner_proccesing" });
        }

        try {
            const response = await AdsAdminApi.uploadBanner(adId, file);
            const result = (await response.json().catch(() => ({}))) as AdsAdminUploadImageResponse;

            if (!response.ok || result.success !== 1) {
                const message =
                    typeof result.message === "string" && result.message ? result.message : "Banner upload failed.";
                notifier.error(message);
                if (status) {
                    status.textContent = "Upload failed.";
                }
                return;
            }

            const imageUrl = (result.data as { image_url?: string } | undefined)?.image_url;
            const previewImg = this.form.getBannerPreviewImg(row);
            if (previewImg instanceof HTMLImageElement && typeof imageUrl === "string" && imageUrl) {
                previewImg.src = imageUrl;
                previewImg.hidden = false;
            }

            if (status) {
                status.textContent = "Banner updated.";
            }
            notifier.success("Banner updated.");
        } catch (error) {
            notifier.error(error instanceof Error && error.message ? error.message : "Banner upload failed.");
            if (status) {
                status.textContent = "Upload failed.";
            }
        } finally {
            input.value = "";
            if (trigger instanceof HTMLButtonElement) {
                spinner.hideButtonLoader(trigger);
            }
        }
    }

    public async submitRow(event: SubmitEvent): Promise<void> {
        event.preventDefault();

        const row = this.form.getRow(event.target);
        if (!(row instanceof HTMLFormElement)) {
            return;
        }

        const submitButton = this.form.getSubmitButton(row);
        const formData = new FormData(row);

        if (submitButton instanceof HTMLButtonElement) {
            spinner.showButtonLoader(submitButton, { translateKey: "spinner_proccesing" });
        }

        try {
            const response = await AdsAdminApi.update(row, formData);
            const result = (await response.json().catch(() => ({}))) as AdsAdminUpdateResponse;

            if (!response.ok || result.success !== 1 || !result.item) {
                notifier.error(this.getErrorMessage(result));
                return;
            }

            this.applyRowValues(row, result.item);
            row.dataset.editing = "0";
            notifier.success(
                typeof result.message === "string" && result.message ? result.message : "Ad updated.",
            );
        } catch (error) {
            notifier.error(error instanceof Error && error.message ? error.message : "Ad update failed.");
        } finally {
            if (submitButton instanceof HTMLButtonElement) {
                spinner.hideButtonLoader(submitButton);
            }
        }
    }

    private getErrorMessage(result: AdsAdminUpdateResponse): string {
        if (typeof result.message === "string" && result.message) {
            return result.message;
        }

        if (result.errors && typeof result.errors === "object") {
            const firstError = Object.values(result.errors).flat()[0];
            if (typeof firstError === "string" && firstError) {
                return firstError;
            }
        }

        return "Ad update failed.";
    }

    private applyRowValues(row: HTMLFormElement, item: NonNullable<AdsAdminUpdateResponse["item"]>): void {
        for (const field of TEXT_FIELDS) {
            this.setFieldValue(row, field, item[field as keyof typeof item] as string | number | null | undefined);
        }

        const adType = typeof item.ad_type === "string" ? item.ad_type : "";
        const adTypeDisplay = this.form.getFieldDisplay(row, "ad_type");
        if (adTypeDisplay) {
            adTypeDisplay.textContent = adType || "-";
            adTypeDisplay.classList.remove(...BADGE_COLOR_CLASSES);
            adTypeDisplay.classList.add(AD_TYPE_BADGE_COLORS[adType] ?? "badge-config--muted");
        }

        const titleDisplay = this.form.getFieldDisplay(row, "ad_title");
        if (titleDisplay) {
            titleDisplay.textContent = typeof item.ad_title === "string" ? item.ad_title : "";
        }

        const bodyDisplay = this.form.getFieldDisplay(row, "ad_body");
        if (bodyDisplay) {
            bodyDisplay.textContent = typeof item.ad_body === "string" ? item.ad_body : "";
        }

        const linkDisplay = this.form.getFieldDisplay(row, "link_url");
        if (linkDisplay) {
            linkDisplay.textContent = typeof item.link_url === "string" ? item.link_url : "";
        }

        const productId = item.ad_product_id ?? null;
        const productIdDisplay = this.form.getFieldDisplay(row, "ad_product_id");
        if (productIdDisplay) {
            productIdDisplay.textContent = productId !== null ? String(productId) : "-";
            productIdDisplay.classList.remove(...BADGE_COLOR_CLASSES);
            productIdDisplay.classList.add(productId ? "badge-config--info" : "badge-config--muted");
        }

        const displayRate = Number(item.display_rate_percent ?? 0);
        const displayRateDisplay = this.form.getFieldDisplay(row, "display_rate_percent");
        if (displayRateDisplay) {
            displayRateDisplay.textContent = `${displayRate}%`;
            displayRateDisplay.classList.remove(...BADGE_COLOR_CLASSES);
            displayRateDisplay.classList.add(displayRate > 0 ? "badge-config--success" : "badge-config--muted");
        }
    }

    private setFieldValue(row: HTMLFormElement, name: string, value: string | number | null | undefined): void {
        const field = this.form.getField(row, name);
        const normalized = value === null || value === undefined ? "" : String(value);

        if (field instanceof HTMLInputElement || field instanceof HTMLTextAreaElement || field instanceof HTMLSelectElement) {
            field.value = normalized;
        }
    }

    public async deleteRow(button: HTMLButtonElement): Promise<void> {
        const row = this.form.getRow(button);
        if (!(row instanceof HTMLFormElement)) {
            return;
        }

        const adId = Number.parseInt(row.dataset.adId || "", 10);
        if (!Number.isFinite(adId) || adId <= 0) {
            return;
        }

        const confirmed = await notifier.confirm("ads_admin_delete_confirm", {
            translateKey: "ads_admin_delete_confirm",
            okTranslateKey: "delete",
            cancelTranslateKey: "cancel",
        });
        if (!confirmed) {
            return;
        }

        spinner.showButtonLoader(button, { translateKey: "spinner_proccesing" });

        try {
            const response = await AdsAdminApi.deleteAd(adId);
            const result = (await response.json().catch(() => ({}))) as AdsAdminDeleteResponse;

            if (!response.ok || result.success !== 1) {
                const message =
                    typeof result.message === "string" && result.message ? result.message : "Ad delete failed.";
                notifier.error(message);
                return;
            }

            row.remove();
            notifier.success("Ad deleted.");
        } catch (error) {
            notifier.error(error instanceof Error && error.message ? error.message : "Ad delete failed.");
        } finally {
            spinner.hideButtonLoader(button);
        }
    }

    public openCreateModal(): void {
        const modal = this.form.getCreateModal();
        if (!(modal instanceof HTMLElement)) {
            return;
        }

        this.resetCreateForm();
        ModalNestedPosition.openModal({ modalEl: modal, displayValue: "block" });
        ScrollLock.lock();
    }

    public closeCreateModal(): void {
        const modal = this.form.getCreateModal();
        if (!(modal instanceof HTMLElement)) {
            return;
        }

        ModalNestedPosition.closeModal({ modalEl: modal });
        ScrollLock.unlock();
        this.resetCreateForm();
    }

    private resetCreateForm(): void {
        const formEl = this.form.getCreateForm();
        if (formEl instanceof HTMLFormElement) {
            formEl.reset();
        }
    }

    public async submitCreateForm(event: SubmitEvent): Promise<void> {
        event.preventDefault();

        const adTypeEl = this.form.getCreateAdTypeSelect();
        const adTitleEl = this.form.getCreateAdTitleInput();
        const adBodyEl = this.form.getCreateAdBodyInput();
        const linkUrlEl = this.form.getCreateLinkUrlInput();
        const productIdEl = this.form.getCreateProductIdInput();
        const displayRateEl = this.form.getCreateDisplayRateInput();
        const bannerEl = this.form.getCreateBannerInput();
        const submitBtn = this.form.getCreateSubmitButton();

        const adType = adTypeEl instanceof HTMLSelectElement ? adTypeEl.value : "";
        if (adType === "") {
            notifier.error("Ad type is required.");
            return;
        }

        const payload: AdsAdminCreatePayload = { ad_type: adType };
        const adTitle = adTitleEl instanceof HTMLInputElement ? adTitleEl.value.trim() : "";
        if (adTitle !== "") payload.ad_title = adTitle;
        const adBody = adBodyEl instanceof HTMLTextAreaElement ? adBodyEl.value.trim() : "";
        if (adBody !== "") payload.ad_body = adBody;
        const linkUrl = linkUrlEl instanceof HTMLInputElement ? linkUrlEl.value.trim() : "";
        if (linkUrl !== "") payload.link_url = linkUrl;
        const productId = productIdEl instanceof HTMLInputElement ? productIdEl.value.trim() : "";
        if (productId !== "") payload.ad_product_id = Number.parseInt(productId, 10);
        const displayRate = displayRateEl instanceof HTMLInputElement ? displayRateEl.value.trim() : "";
        if (displayRate !== "") payload.display_rate_percent = Number.parseInt(displayRate, 10);

        try {
            if (submitBtn instanceof HTMLButtonElement) {
                spinner.showButtonLoader(submitBtn, { translateKey: "spinner_proccesing" });
            }

            const response = await AdsAdminApi.create(payload);
            const result = (await response.json().catch(() => ({}))) as AdsAdminCreateResponse;

            if (!response.ok || result.success !== 1) {
                const message =
                    typeof result.message === "string" && result.message ? result.message : "Ad create failed.";
                notifier.error(message);
                return;
            }

            const createdAdId = (result.data as { item?: { ad_id?: number } } | undefined)?.item?.ad_id;
            const bannerFile = bannerEl instanceof HTMLInputElement ? bannerEl.files?.[0] : undefined;

            if (typeof createdAdId === "number" && bannerFile) {
                await AdsAdminApi.uploadBanner(createdAdId, bannerFile);
            }

            notifier.success("Ad created.");
            this.closeCreateModal();
            window.location.reload();
        } catch (error) {
            notifier.error(error instanceof Error && error.message ? error.message : "Ad create failed.");
        } finally {
            if (submitBtn instanceof HTMLButtonElement) {
                spinner.hideButtonLoader(submitBtn);
            }
        }
    }
}
